import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Receipt, Check, X, Plus, Download, Trash2, TrendingUp, Wallet, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, API, formatApiError, brl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function OrderDialog({ order, onClose }) {
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="order-detail-dialog" aria-describedby={undefined} className="max-w-md rounded-3xl">
        <DialogHeader><DialogTitle className="font-display text-xl">Comanda — {order?.user_name || "Venda no local"}</DialogTitle></DialogHeader>
        {order && (
          <div>
            <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString("pt-BR")} · {order.status}</p>
            <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-200">
              {order.items.map((i, idx) => (
                <div key={idx} data-testid={`order-detail-item-${idx}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span className="min-w-0 text-slate-700"><strong>{i.qty}x</strong> {i.name} <span className="text-xs text-slate-400">({brl(i.price)})</span></span>
                  <span className="shrink-0 font-semibold text-slate-900">{brl(i.qty * (i.price || 0))}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between rounded-2xl bg-emerald-50 px-4 py-3">
              <span className="font-semibold text-slate-700">Total</span>
              <span data-testid="order-detail-total" className="font-display text-xl font-black text-[#1B7A45]">{brl(order.total)}</span>
            </div>
            {order.note && <p className="mt-2 text-xs text-slate-500">Obs.: {order.note}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const RANGES = [
  { id: "6h", label: "6 HRS" },
  { id: "9h", label: "9 HRS" },
  { id: "12h", label: "12 HRS" },
  { id: "24h", label: "24 HRS" },
  { id: "7d", label: "7 DIAS" },
  { id: "15d", label: "15 DIAS" },
  { id: "month", label: "ESTE MÊS" },
  { id: "all", label: "TUDO" },
];

const fmtTime = (iso) => new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function BarOrdersPanel() {
  const [range, setRange] = useState("24h");
  const [orders, setOrders] = useState([]);
  const [report, setReport] = useState({ revenue: 0, cost: 0, profit: 0, count: 0 });
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [sale, setSale] = useState({ user_id: "", items: {} });
  const [viewOrder, setViewOrder] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/bar/orders", { params: { range } });
      setOrders(res.data);
      const rep = await api.get("/admin/bar/report", { params: { range } });
      setReport(rep.data);
    } catch (e) { /* ignore */ }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/bar/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const act = async (id, action) => {
    try { await api.post(`/admin/bar/orders/${id}/${action}`); await load(); toast.success(action === "confirm" ? "Pagamento confirmado!" : "Pedido cancelado."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Excluir pedido?")) return; try { await api.delete(`/admin/bar/orders/${id}`); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/admin/bar/report.pdf", { params: { range }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `relatorio-bar-${range}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Não foi possível gerar o PDF."); }
  };

  const saleItems = useMemo(() => products.filter((p) => sale.items[p.id]), [products, sale]);
  const saleTotal = useMemo(() => saleItems.reduce((s, p) => s + p.price * sale.items[p.id], 0), [saleItems, sale]);
  const incItem = (id) => setSale((s) => ({ ...s, items: { ...s.items, [id]: (s.items[id] || 0) + 1 } }));
  const decItem = (id) => setSale((s) => { const n = (s.items[id] || 0) - 1; const it = { ...s.items }; if (n <= 0) delete it[id]; else it[id] = n; return { ...s, items: it }; });

  const submitSale = async () => {
    if (saleItems.length === 0) return toast.error("Selecione ao menos um produto.");
    try {
      await api.post("/admin/bar/orders", { user_id: sale.user_id, items: saleItems.map((p) => ({ product_id: p.id, qty: sale.items[p.id] })), status: "confirmada" });
      setShowAdd(false); setSale({ user_id: "", items: {} });
      await load();
      toast.success("Venda registrada!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-8" data-testid="bar-orders-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-[#1B7A45]" />
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Bar — Vendas</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <button key={r.id} data-testid={`barrange-${r.id}`} onClick={() => setRange(r.id)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${range === r.id ? "bg-[#1B7A45] text-white" : "bg-slate-100 text-slate-600"}`}>{r.label}</button>
          ))}
          <Button data-testid="bar-add-sale" onClick={() => setShowAdd((v) => !v)} size="sm" className="rounded-full bg-[#E8611A] text-white hover:bg-[#CF5212]"><Plus className="mr-1 h-4 w-4" /> Venda local</Button>
          <Button data-testid="bar-pdf" onClick={downloadPdf} size="sm" variant="outline" className="rounded-full border-slate-200"><Download className="mr-1 h-4 w-4" /> PDF</Button>
        </div>
      </div>

      {/* Report cards */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><Wallet className="mb-1 h-5 w-5 text-[#1B7A45]" /><p data-testid="report-revenue" className="text-xl font-black text-slate-900">{brl(report.revenue)}</p><p className="text-xs text-slate-500">Faturamento</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><Coins className="mb-1 h-5 w-5 text-amber-600" /><p data-testid="report-cost" className="text-xl font-black text-slate-900">{brl(report.cost)}</p><p className="text-xs text-slate-500">Custo</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><TrendingUp className="mb-1 h-5 w-5 text-emerald-600" /><p data-testid="report-profit" className="text-xl font-black text-[#1B7A45]">{brl(report.profit)}</p><p className="text-xs text-slate-500">Lucro</p></div>
      </div>

      {/* Add local sale */}
      {showAdd && (
        <div data-testid="add-sale-box" className="mt-3 overflow-hidden rounded-2xl border border-[#E8611A]/30 bg-orange-50/50 p-3">
          <div className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
            <span className="shrink-0 text-sm font-semibold text-slate-600">Cliente:</span>
            <select data-testid="sale-user" value={sale.user_id} onChange={(e) => setSale({ ...sale, user_id: e.target.value })} className="w-full min-w-0 max-w-full truncate rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm sm:flex-1">
              <option value="">Venda no local (sem usuário)</option>
              {[...users].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR")).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}{u.phone ? ` — ${u.phone}` : ""}</option>)}
            </select>
          </div>
          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {products.map((p) => (
              <div key={p.id} className="flex min-w-0 items-center justify-between gap-2 rounded-xl bg-white px-3 py-1.5">
                <span className="min-w-0 truncate text-sm text-slate-700">{p.name} <span className="text-xs text-slate-400">{brl(p.price)}</span></span>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button onClick={() => decItem(p.id)} className="rounded bg-slate-100 px-2 font-bold">−</button>
                  <span className="w-5 text-center text-sm font-bold">{sale.items[p.id] || 0}</span>
                  <button data-testid={`sale-add-${p.id}`} onClick={() => incItem(p.id)} className="rounded bg-[#1B7A45] px-2 font-bold text-white">+</button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="font-display text-lg font-black text-[#E8611A]">{brl(saleTotal)}</span>
            <Button data-testid="sale-submit" onClick={submitSale} className="rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]">Registrar venda</Button>
          </div>
        </div>
      )}

      {/* Orders list */}
      <div className="mt-4 space-y-2">
        {orders.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-8 text-center text-sm text-slate-400">Nenhum pedido no período.</p> :
          orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid={`order-open-${o.id}`} onClick={() => setViewOrder(o)} className="font-display font-bold text-slate-900 underline-offset-2 hover:text-[#1B7A45] hover:underline">{o.user_name || "Venda no local"}</button>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${o.status === "confirmada" ? "bg-emerald-100 text-emerald-700" : o.status === "cancelada" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                  {o.source === "admin" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">local</span>}
                </div>
                <p className="truncate text-xs text-slate-500">{o.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</p>
                <p data-testid={`order-time-${o.id}`} className="text-[11px] text-slate-400">Comprado em {fmtTime(o.created_at)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="font-display font-black text-[#1B7A45]">{brl(o.total)}</span>
                {o.status === "pendente" && <Button data-testid={`order-confirm-${o.id}`} onClick={() => act(o.id, "confirm")} size="sm" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"><Check className="h-4 w-4" /></Button>}
                {o.status !== "cancelada" && <Button data-testid={`order-cancel-${o.id}`} onClick={() => act(o.id, "cancel")} size="sm" variant="outline" className="rounded-full border-amber-200 text-amber-700"><X className="h-4 w-4" /></Button>}
                <Button data-testid={`order-del-${o.id}`} onClick={() => del(o.id)} size="sm" variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
      </div>
      <OrderDialog order={viewOrder} onClose={() => setViewOrder(null)} />
    </div>
  );
}
